// Cabelos. GABARITO da Onda 0: 'careca' e 'curto' são os modelos; os demais ids de
// HAIR_IDS (src/core/look.ts) seguem o mesmo formato.
//
// Quadro 16×12 por cima da cabeça (ver bodies.ts para onde ficam olhos e orelhas: olhos
// nas colunas 5 e 10, linhas 6–7; contorno da cabeça nas colunas 2 e 13). De lado (left),
// o olho fica na coluna 4. Nunca cobrir os olhos: só a franja pode descer até a linha 5.
// Paleta: o = hair0 (contorno), d = hair1, h = hair2, H = hair3 (luz do alto-esquerda).
// Cabelo comprido usa `behind` (16×24) para a parte que cai atrás dos ombros: fica ATRÁS
// do corpo, então só aparece nas colunas de fora dos braços. Começa na linha 4, sob a cabeça,
// para descer contínuo da lateral da cabeça.

import { sprite } from '../pix.js';
import type { DirSet, OverlayDef, PixelSprite } from '../types.js';

const HAIR = { o: 'hair0', d: 'hair1', h: 'hair2', H: 'hair3' };
const EMPTY: PixelSprite = sprite(Array.from({ length: 12 }, () => '.'.repeat(16)), {});

/** Um cabelo a partir de quadros digitados: cabeça 16×12 e, se for comprido, `behind` 16×24. */
function hair(id: string, name: string, head: DirSet<readonly string[]>, behind?: DirSet<readonly string[]>): OverlayDef {
  const frames = { down: sprite(head.down, HAIR), up: sprite(head.up, HAIR), left: sprite(head.left, HAIR) };
  if (!behind) return { id, name, anchor: 'head', frames };
  const back = { down: sprite(behind.down, HAIR), up: sprite(behind.up, HAIR), left: sprite(behind.left, HAIR) };
  return { id, name, anchor: 'head', frames, behind: back };
}

// Cabelo que cai atrás do corpo (quadro 16×24). `sides` põe mechas nas duas laterais, de fora
// para dentro; `at` põe mechas a partir de uma coluna. O corpo tapa o que estiver atrás dele, então
// só aparece o que passa das colunas dos braços. A mecha começa na linha 4, sob a cabeça, e afina
// até a ponta (linha 19).
const sides = (l: string, r: string): string => l + '.'.repeat(16 - l.length - r.length) + r;
const at = (x: number, s: string): string => '.'.repeat(x) + s + '.'.repeat(16 - x - s.length);
const blank = (n: number): string[] => Array.from({ length: n }, () => '................');

// Liso: duas mechas retas, uma de cada lado, da cabeça até as pontas.
const BACK_FRONT_STRAIGHT: readonly string[] = [
  ...blank(4),
  ...Array.from({ length: 8 }, () => sides('odh', 'hdo')),
  ...Array.from({ length: 6 }, () => sides('odh', 'hdo')),
  sides('od', 'do'),
  sides('o', 'o'),
  ...blank(4),
];
const BACK_LEFT_STRAIGHT: readonly string[] = [
  ...blank(4),
  ...Array.from({ length: 8 }, () => at(13, 'hdo')),
  ...Array.from({ length: 6 }, () => at(12, 'hdo')),
  at(13, 'do'),
  at(13, 'o'),
  ...blank(4),
];
// Ondulado: as mechas alternam a largura, como ondas.
const BACK_FRONT_WAVY: readonly string[] = [
  ...blank(4),
  ...Array.from({ length: 8 }, (_, i) => (i % 2 ? sides('od', 'do') : sides('odh', 'hdo'))),
  ...Array.from({ length: 6 }, (_, i) => (i % 2 ? sides('odh', 'hdo') : sides('od', 'do'))),
  sides('od', 'do'),
  sides('o', 'o'),
  ...blank(4),
];
const BACK_LEFT_WAVY: readonly string[] = [
  ...blank(4),
  ...Array.from({ length: 8 }, (_, i) => (i % 2 ? at(14, 'do') : at(13, 'hdo'))),
  ...Array.from({ length: 6 }, (_, i) => (i % 2 ? at(13, 'hdo') : at(12, 'hdo'))),
  at(13, 'do'),
  at(13, 'o'),
  ...blank(4),
];
// Trança e dreads: mechas que caem em blocos de três colunas de cada lado.
const ROPES_A = sides('ohd', 'dho');
const ROPES_B = sides('dho', 'ohd');
const BACK_FRONT_ROPES: readonly string[] = [
  ...blank(4),
  ...Array.from({ length: 8 }, (_, i) => (i % 2 ? ROPES_B : ROPES_A)),
  ...Array.from({ length: 6 }, (_, i) => (i % 2 ? ROPES_A : ROPES_B)),
  sides('od', 'do'),
  sides('o', 'o'),
  ...blank(4),
];
const BACK_LEFT_ROPES: readonly string[] = [
  ...blank(4),
  ...Array.from({ length: 8 }, (_, i) => at(13, i % 2 ? 'dho' : 'ohd')),
  ...Array.from({ length: 6 }, (_, i) => at(12, i % 2 ? 'ohd' : 'dho')),
  at(13, 'do'),
  at(13, 'o'),
  ...blank(4),
];
export const HAIRS: readonly OverlayDef[] = [
  { id: 'careca', name: 'Careca', anchor: 'head', frames: { down: EMPTY, up: EMPTY, left: EMPTY } },
  {
    id: 'curto',
    name: 'Curto',
    anchor: 'head',
    frames: {
      down: sprite(
        [
          '....oooooooo....',
          '...oHHHhhhhdo...',
          '..oHHhhhhhhhdo..',
          '..ohHhhhhhhhdo..',
          '..ohhd....dhdo..',
          '..ohd......dho..',
          '..od........do..',
          '................',
          '................',
          '................',
          '................',
          '................',
        ],
        HAIR,
      ),
      up: sprite(
        [
          '....oooooooo....',
          '...oHHHhhhhdo...',
          '..oHHhhhhhhhdo..',
          '..oHhhhhhhhhdo..',
          '..ohhhhhhhhhdo..',
          '..ohhhhhhhhhdo..',
          '..ohhhhhhhhddo..',
          '..odhhhhhhhddo..',
          '...odhhhhhddo...',
          '....oddddddo....',
          '................',
          '................',
        ],
        HAIR,
      ),
      left: sprite(
        [
          '....oooooooo....',
          '...oHHhhhhhhdo..',
          '..oHhhhhhhhhhdo.',
          '..ohhhhhhhhhhdo.',
          '..ohd...dhhhhdo.',
          '..od.....dhhhdo.',
          '..........dhhdo.',
          '...........ddo..',
          '................',
          '................',
          '................',
          '................',
        ],
        HAIR,
      ),
    },
  },
  // Raspado: quase só a sombra da cabeça raspada, bem rente.
  hair(
    'raspado',
    'Raspado',
    {
      down: [
        '................',
        '.....oooooo.....',
        '....oHhhhhdo....',
        '...oHhhhhhhdo...',
        '...oddddddddo...',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '................',
        '.....oooooo.....',
        '....oHhhhhdo....',
        '...oHhhhhhhdo...',
        '...oddddddddo...',
        '..oddddddddddo..',
        '..odddddddddddo.',
        '..odddddddddddo.',
        '...odddddddddo..',
        '................',
        '................',
        '................',
      ],
      left: [
        '................',
        '.....oooooo.....',
        '....oHHhhhhdo...',
        '...oHhhhhhhdo...',
        '...oddddddddo...',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Topete: volume alto na frente, laterais curtas.
  hair(
    'topete',
    'Topete',
    {
      down: [
        '....oHHHHHHo....',
        '...oHHHhhhhdo...',
        '..oHHhhhhhhhdo..',
        '..ohHhhhhhhhdo..',
        '..ohhhhhhhhhdo..',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '....oooooooo....',
        '...odhhhhhhdo...',
        '..odhhhhhhhhdo..',
        '..odhhhhhhhhdo..',
        '..odhhhhhhhhdo..',
        '...odhhhhhhdo...',
        '....oddddddo....',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      left: [
        '....oHHHHHHo....',
        '...oHHhhhhhdo...',
        '..oHhhhhhhhhdo..',
        '..ohhhhhhhhhdo..',
        '..ohhhhhhhhhdo..',
        '..ohhhhhhhhdo...',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Lateral: risca de lado, a franja cai sobre a testa e o lado longo desce.
  hair(
    'lateral',
    'Lateral',
    {
      down: [
        '....oooooooo....',
        '...oHHHHhhhdo...',
        '..oHHHhhhhhhdo..',
        '..oHHhhhhhhhdo..',
        '..ohhhhhhhhhdo..',
        '...ohhhhhhhdo...',
        '..........dhdo..',
        '...........do...',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '....oooooooo....',
        '...oHhhhhhhdo...',
        '..oHhhhhhhhhdo..',
        '..oHhhhhhhhhdo..',
        '..ohhhhhhhhhdo..',
        '..ohhhhhhhhhdo..',
        '..ohhhhhhhhddo..',
        '...odhhhhhddo...',
        '....oddddddo....',
        '................',
        '................',
        '................',
      ],
      left: [
        '....oooooooo....',
        '...oHHHhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '...ohhhhhhdo....',
        '...........ddo..',
        '................',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Cacheado: cachos em bolinhas no topo e nas laterais, com sombra entre eles.
  hair(
    'cacheado',
    'Cacheado',
    {
      down: [
        '...oo.oooo.oo...',
        '..oHoHHhhHoHho..',
        '.oHHhoHHHhoHHho.',
        '.ohHHhhhhhhhhdo.',
        '.ohhdhhhhhhdhdo.',
        '..oHhdo..odhho..',
        '..ohhdo....ohho.',
        '..odhdo....oho..',
        '...oo.........o.',
        '................',
        '................',
        '................',
      ],
      up: [
        '...oo.oooo.oo...',
        '..oHoHHHHHoHdo..',
        '.oHHhoHHHHoHhdo.',
        '.ohhdhhhhhhhhdo.',
        '.ohhdhhhhhhhdhdo',
        '.ohhdhhhhhhhhdo.',
        '.ohhhdhhhhhhdho.',
        '..odhhhhhhdhdo..',
        '...odddddddo....',
        '................',
        '................',
        '................',
      ],
      left: [
        '...oo.oooo.oo...',
        '..oHoHHHhhoHdo..',
        '.oHHhoHHHhhoHdo.',
        '.ohHhhhhhhhhhdo.',
        '.ohhdhhhhhhhhdo.',
        '..oHhdo.ohhhdo..',
        '..ohhdo..odhdo..',
        '..odhdo...odo...',
        '...oo...........',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Crespo-curto: cabelo rente e denso, textura de pontinhos.
  hair(
    'crespo-curto',
    'Crespo curto',
    {
      down: [
        '.....oooooo.....',
        '...ooHdHhdHoo...',
        '..oHdHhHdhHhdo..',
        '..ohHdhHdhHdho..',
        '..odhdHhdhdHdo..',
        '..odhdhd..dhdo..',
        '..od.dh....d.o..',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '.....oooooo.....',
        '...ooHdHhdHoo...',
        '..oHdHhHdhHhdo..',
        '..ohHdhHdhHdhdo.',
        '..odhdHhdhdHdho.',
        '..odhdhdhdhdhdo.',
        '..odhdhdhdhdhdo.',
        '..odhdhdhdhdhdo.',
        '...odhdhdhdhdo..',
        '....oddddddo....',
        '................',
        '................',
      ],
      left: [
        '.....oooooo.....',
        '...ooHdHhdHo....',
        '..oHdHhHdhHho...',
        '..ohHdhHdhHdhdo.',
        '..ohdhdHhdhdHdo.',
        '..od.dhdhdhdhdo.',
        '..o..dh.dhdhdo..',
        '..........dd.o..',
        '................',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Black power: volume de cacho cheio; nas laterais, só até o queixo e sem tocar os olhos.
  hair(
    'black-power',
    'Black power',
    {
      down: [
        '..oooooooooooo..',
        '.oHhdhHhdhHhdho.',
        'oHhhdhHhdhHhdhdo',
        'ohhhdhHhdhHhdhdo',
        'ohdhhhdhHhdhhhdo',
        'odhhhdhhhhhdhhdo',
        'odhh........hhdo',
        'odhh........hhdo',
        'ohdh........hdho',
        '.odhhdhdhdhdhdo.',
        '..oddddddddddo..',
        '................',
      ],
      up: [
        '..oooooooooooo..',
        '.oHhdhHhdhHhdho.',
        'oHhhdhHhdhHhdhdo',
        'ohhhdhHhdhHhdhdo',
        'ohdhhhdhHhdhhhdo',
        'odhhhdhhhhhdhhdo',
        'odhhhdhhhhhdhhdo',
        'odhdhhdhhhdhhdho',
        '.odhhdhhhdhhdho.',
        '..oddddddddddo..',
        '................',
        '................',
      ],
      left: [
        '..oooooooooooo..',
        '.oHhdhHhdhHhdho.',
        'oHhhdhHhdhHhdhdo',
        'ohhhdhHhdhHhdhdo',
        'ohdhhhdhHhdhhhdo',
        'ohhhhdhhhhhhdhdo',
        'odhh........hhdo',
        'odhh........hhdo',
        'ohdh........hdho',
        '.odhhdhhhdhhdho.',
        '..oddddddddddo..',
        '................',
      ],
    },
  ),
  // Moicano: crista central alta e lados raspados.
  hair(
    'moicano',
    'Moicano',
    {
      down: [
        '......oooooo....',
        '.....oHHhhhdo...',
        '.....oHhhhhdo...',
        '.....oHhhhhdo...',
        '.....oHhhhhdo...',
        '......oddddo....',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '......oooooo....',
        '.....odhhhhdo...',
        '.....odhhhhdo...',
        '.....odhhhhdo...',
        '.....odhhhhdo...',
        '......oddddo....',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      left: [
        '......oooooo....',
        '.....oHHhhhdo...',
        '.....oHhhhhdo...',
        '.....oHhhhhdo...',
        '.....oHhhhhdo...',
        '......oddddo....',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Franja: cabelo cheio na testa; a franja desce até a linha 5 e para acima dos olhos.
  hair(
    'franja',
    'Franja',
    {
      down: [
        '....oooooooo....',
        '...oHHHhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..ohHhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohdhhhhhhhhdo.',
        '..od..........o.',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '....oooooooo....',
        '...oHhhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhddo..',
        '...odhhhhhddo...',
        '....oddddddo....',
        '................',
        '................',
        '................',
      ],
      left: [
        '....oooooooo....',
        '...oHHHhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '..o.hhhhhhdo....',
        '...........do...',
        '................',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Chanel: franja reta e bob até o queixo, colado no rosto.
  hair(
    'chanel',
    'Chanel',
    {
      down: [
        '....oooooooo....',
        '...oHHHhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..ohHhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohdhhhhhhhdo..',
        '..odo........do.',
        '..odo........do.',
        '..odo........do.',
        '..oo..........o.',
        '................',
        '................',
      ],
      up: [
        '....oooooooo....',
        '...oHhhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..odhhhhhhhhddo.',
        '..oddddddddddo..',
        '...oo........oo.',
        '................',
        '................',
      ],
      left: [
        '....oooooooo....',
        '...oHHHhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '..o.hhhhhhhdo...',
        '..o.hhhhhhdo....',
        '...o..dhhdo.....',
        '....oo.oo.......',
        '................',
        '................',
      ],
    },
  ),
  // Longo liso: topo liso com risca e o comprimento atrás dos ombros.
  hair(
    'longo-liso',
    'Longo liso',
    {
      down: [
        '....oooooooo....',
        '...oHHhhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..ohHhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohdhhhhhhhdo..',
        '..odo........do.',
        '..odo........do.',
        '..odo........do.',
        '..odo........do.',
        '..oo..........o.',
        '................',
      ],
      up: [
        '....oooooooo....',
        '...oHhhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..odhhhhhhhhddo.',
        '..oddddddddddo..',
        '...oo........oo.',
        '................',
        '................',
      ],
      left: [
        '....oooooooo....',
        '...oHHhhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '..o.hhhhhhhdo...',
        '..o.hhhhhhdo....',
        '...o..dhhdo.....',
        '....oo.oo.......',
        '................',
        '................',
      ],
    },
    { down: BACK_FRONT_STRAIGHT, up: BACK_FRONT_STRAIGHT, left: BACK_LEFT_STRAIGHT },
  ),
  // Longo ondulado: a risca é lateral e as pontas fazem ondas.
  hair(
    'longo-ondulado',
    'Longo ondulado',
    {
      down: [
        '....oooooooo....',
        '...oHhhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhhdo',
        '..odhhhhhhhhhdo.',
        '...ohhhhhhhhdo..',
        '..od........dho.',
        '..od........dho.',
        '...od......do...',
        '................',
        '................',
        '................',
      ],
      up: [
        '....oooooooo....',
        '...oHhhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhddo..',
        '...odhhhhhddo...',
        '....oddddddo....',
        '................',
        '................',
        '................',
      ],
      left: [
        '....oooooooo....',
        '...oHHhhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '..od........dho.',
        '..od........dho.',
        '...od......do...',
        '................',
        '................',
        '................',
      ],
    },
    { down: BACK_FRONT_WAVY, up: BACK_FRONT_WAVY, left: BACK_LEFT_WAVY },
  ),
  // Rabo de cavalo: cabelo puxado para trás, sem franja; o rabo cai atrás da cabeça.
  hair(
    'rabo-de-cavalo',
    'Rabo de cavalo',
    {
      down: [
        '....oooooooo....',
        '...oHhhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..ohHhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '..ohhhhhhhhhdo..',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '....oooooooo....',
        '...oHhhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..odhhhhhhhhddo.',
        '..oddddddddddo..',
        '.....oddoo......',
        '.....oddo.......',
        '......oo........',
      ],
      left: [
        '....oooooooo....',
        '...oHHhhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '..ohhhhhhhhdo...',
        '...ohhhhhdo.....',
        '....oddddo......',
        '................',
        '................',
        '................',
      ],
    },
    {
      down: [...blank(4), ...Array.from({ length: 12 }, () => at(13, 'ohd')), at(13, 'do'), at(13, 'do'), at(14, 'o'), ...blank(5)],
      up: [...blank(24)],
      left: [...blank(4), ...Array.from({ length: 12 }, () => at(13, 'ohd')), at(13, 'do'), at(13, 'do'), at(14, 'o'), ...blank(5)],
    },
  ),
  // Coque: cabelo preso num coque no alto da cabeça.
  hair(
    'coque',
    'Coque',
    {
      down: [
        '.....oooooo.....',
        '....oHHhhhho....',
        '...oHhhhhhhdo...',
        '..oHhhhhhhhhdo..',
        '..ohhhhhhhhhdo..',
        '..ohhdhhhhhhdo..',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '.....oooooo.....',
        '....odhhhhdo....',
        '...odhhhhhhdo...',
        '..odhhhhhhhhdo..',
        '..odhhhhhhhhdo..',
        '..odhhhhhhhhdo..',
        '..odhhhhhhhhdo..',
        '...odhhhhhhdo...',
        '....oddddddo....',
        '................',
        '................',
        '................',
      ],
      left: [
        '.....oooooo.....',
        '....oHHhhhho....',
        '...oHHhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '..od........dho.',
        '..od........dho.',
        '...od......do...',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Trancas: duas tranças caindo dos lados, atrás dos ombros.
  hair(
    'trancas',
    'Trancas',
    {
      down: [
        '....oooooooo....',
        '...oHHhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhhdo',
        'ohdhhhhhhhhhhdho',
        'hdo..........ohd',
        'ohd..........dho',
        'dho..........ohd',
        'ohd..........dho',
        'dho..........ohd',
        'ohd..........dho',
        'dho..........ohd',
      ],
      up: [
        '....oooooooo....',
        '...oHhhhhhhhdo..',
        '..oHhhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..odhhhhhhhhddo.',
        '..oddddddddddo..',
        '...oo........oo.',
        '................',
        '................',
      ],
      left: [
        '....oooooooo....',
        '...oHHhhhhhhdo..',
        '..oHHhhhhhhhhdo.',
        '..oHhhhhhhhhhdo.',
        '..ohhhhhhhhhhdo.',
        '..ohhhhhhhhhdo..',
        '..od........dho.',
        '..od........dho.',
        '...od......do...',
        '................',
        '................',
        '................',
      ],
    },
    { down: BACK_FRONT_ROPES, up: BACK_FRONT_ROPES, left: BACK_LEFT_ROPES },
  ),
  // Maria-chiquinha: dois rabinhos altos nas laterais do alto da cabeça.
  hair(
    'maria-chiquinha',
    'Maria-chiquinha',
    {
      down: [
        '.oo.oooooooo.oo.',
        'oHHoHhhhhhhhoHHo',
        'oHhoHhhhhhhhoHho',
        '.oHoHhhhhhhhhoHo',
        '..oohhhhhhhdoo..',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '.oo.oooooooo.oo.',
        'oHHodhhhhhhdoHHo',
        'oHhodhhhhhhdoHho',
        '..oddhhhhhhddo..',
        '..odhhhhhhhhdo..',
        '..odhhhhhhhhdo..',
        '...odhhhhhhdo...',
        '....oddddddo....',
        '................',
        '................',
        '................',
        '................',
      ],
      left: [
        '....oooooooo.oo.',
        '...oHHhhhhhhdoHo',
        '..oHhhhhhhhhdoHo',
        '..ohhhhhhhhhdoho',
        '..ohhhhhhhhhdo..',
        '..ohhhhhhhhdo...',
        '..od........dho.',
        '..od........dho.',
        '...od......do...',
        '................',
        '................',
        '................',
      ],
    },
  ),
  // Dreads: mechas grossas e separadas que caem do topo até os ombros.
  hair(
    'dreads',
    'Dreads',
    {
      down: [
        '....oooooooo....',
        '...odhodhodhod..',
        '..odhodhodhodho.',
        '..ohdohdohdohdo.',
        '..odhodhodhodho.',
        '..ohdohdohdohdo.',
        '..od..........o.',
        '................',
        '................',
        '................',
        '................',
        '................',
      ],
      up: [
        '....oooooooo....',
        '...odhodhodhod..',
        '..odhodhodhodho.',
        '..ohdohdohdohdo.',
        '..odhodhodhodho.',
        '..ohdohdohdohdo.',
        '..odhodhodhodho.',
        '...odhodhodhdo..',
        '....oddddddo....',
        '................',
        '................',
        '................',
      ],
      left: [
        '....oooooooo....',
        '...odhodhodhod..',
        '..odhodhodhodho.',
        '..ohdohdohdohdo.',
        '..odhodhodhodho.',
        '..od..........o.',
        '..od........dho.',
        '..od........dho.',
        '...od......do...',
        '................',
        '................',
        '................',
      ],
    },
    { down: BACK_FRONT_ROPES, up: BACK_FRONT_ROPES, left: BACK_LEFT_ROPES },
  ),
];
